import Tab from './tab'
import BaseComponent from '../base-component/base-component'
import { checkEnterKey } from '../../utils/utils'
import { getTemplateContent } from '../../utils/dom'
import { Nullable } from '../../types/common'

class Tabs extends BaseComponent {
  containerSelector: string
  templateSelector: string
  tabSelector: string
  tabNameSelector: string

  container: Nullable<HTMLElement>
  template: Nullable<HTMLElement>
  templateContent: any
  tabNames: Element[]
  tabMap: any

  constructor() {
    super()

    this.containerSelector = `[data-tabs-container]`
    this.templateSelector = `[data-tabs-elements-template]`
    this.tabSelector = `tab-component, tab-with-dynamic-height`
    this.tabNameSelector = `[data-tabs-name]`

    this.container = this.querySelector(this.containerSelector)
    this.template = this.querySelector(this.templateSelector)
    this.templateContent = getTemplateContent(this.template)

    this.tabNames = [...this.querySelectorAll(this.tabNameSelector)]
    this.tabMap = this.generateTabMap()
  }

  connectedCallback(): void {
    this.tabNames.forEach(tabName => {
      tabName.addEventListener('keydown', this.handleKeyDown)
      tabName.addEventListener('click', this.handleTabNameClick)
    })
  }

  disconnectedCallback(): void {
    this.tabNames.forEach(tabName => {
      tabName.removeEventListener('keydown', this.handleKeyDown)
      tabName.removeEventListener('click', this.handleTabNameClick)
    })
  }

  handleTabNameClick = (event: MouseEvent): void => {
    event.preventDefault()

    const target = event.target as HTMLElement

    if (target.dataset.id) {
      this.selectTab(target.dataset.id)
    }
  }

  handleKeyDown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement

    if (!target.dataset.id) {
      return
    }

    const isEnterKey = checkEnterKey(event.key || event.keyCode)

    if (isEnterKey) {
      event.preventDefault()

      this.selectTab(target.dataset.id)
    }
  }

  generateTabMap() {
    const tabs = [
      this.querySelector(this.tabSelector),
      ...this.templateContent.querySelectorAll(this.tabSelector),
    ]

    const tabMap = tabs.reduce((map, tab) => {
      const tabId = tab.dataset.id

      return tabId ? { ...map, [tabId]: tab } : map
    }, {})

    return tabMap
  }

  selectTab(tabId: string): void {
    this.updateNameById(tabId)

    this.setTabById(tabId)

    this.emit('selectTab', tabId)
  }

  unmountTab(tab: Tab): void {
    tab.remove()
  }

  mountTab(tab: Tab): void {
    if (!this.container) {
      return
    }

    this.container.appendChild(tab)
  }

  updateNameById(tabId: string): void {
    this.tabNames.forEach(tabName => {
      const isSelected = tabId === tabName.getAttribute('data-id')

      tabName.classList.toggle('selected', isSelected)
    })
  }

  setTabById(tabId: string): void {
    const nextTab = this.tabMap[tabId]
    const currentTab = this.tabMap[this.dataset.selectedTabId]

    if (!currentTab || !nextTab) {
      return
    }

    this.showNextTab(nextTab)

    if (currentTab !== nextTab) {
      this.hideCurrentTab(currentTab)
    }
  }

  showNextTab(tab: Tab): void {
    const id = tab.getAttribute('data-id')

    if (id) {
      this.mountTab(tab)

      tab.show()

      this.dataset.selectedTabId = id
    }
  }

  hideCurrentTab(tab: Tab): void {
    tab.hide().then(() => {
      this.unmountTab(tab)
    })
  }
}

export default Tabs
